/**
 * PRODUCT_CATEGORY targets carry no Amazon match type — the specificity lives in
 * the refinement columns instead (MER-2293). These helpers turn those columns
 * into the one agreed display string:
 *
 *   Category: Kitchen | Brand: Acme | Price: $10-$30 | Rating: 0-4 | Prime
 */

const numberOrNull = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null

    const parsed = parseFloat(value)

    return Number.isFinite(parsed) ? parsed : null
}

const int = (value: number): string => value.toFixed(0)

/**
 * Builds the refinement string for a target row. Returns '' when the row has no
 * resolved product category, so callers can treat '' as "not a category target".
 */
export const formatTargetRefinements = (target: any): string => {
    if (!target?.target_details_product_category_resolved) return ''

    const parts: string[] = [`Category: ${target.target_details_product_category_resolved}`]

    if (target.target_details_product_brand_resolved) {
        parts.push(`Brand: ${target.target_details_product_brand_resolved}`)
    }

    const priceGt = numberOrNull(target.target_details_product_price_greater_than)
    const priceLt = numberOrNull(target.target_details_product_price_less_than)

    if (priceGt !== null && priceLt !== null) {
        parts.push(`Price: $${int(priceGt)}-$${int(priceLt)}`)
    } else if (priceGt !== null) {
        parts.push(`Price > $${int(priceGt)}`)
    } else if (priceLt !== null) {
        parts.push(`Price < $${int(priceLt)}`)
    }

    const ratingGt = numberOrNull(target.target_details_product_rating_greater_than)
    const ratingLt = numberOrNull(target.target_details_product_rating_less_than)

    if (ratingGt !== null && ratingLt !== null) {
        parts.push(`Rating: ${int(ratingGt)}-${int(ratingLt)}`)
    } else if (ratingGt !== null) {
        parts.push(`Rating > ${int(ratingGt)}`)
    } else if (ratingLt !== null) {
        parts.push(`Rating < ${int(ratingLt)}`)
    }

    if (target.target_details_product_prime_shipping_eligible === true) {
        parts.push('Prime')
    }

    return parts.join(' | ')
}

/**
 * The match type to display for a target row, or null when the row keeps its own
 * value. Only a target whose Amazon match type is absent AND whose category is
 * resolved gets a synthesized string — a real match type is never overwritten,
 * and keywords are never touched.
 */
export const syntheticTargetMatchType = (target: any): string | null => {
    if (!target) return null
    if (target.target_details_keyword) return null

    const matchType = target.target_details_match_type ?? target.match_type

    if (matchType !== null && matchType !== undefined && matchType !== '') return null

    const refinements = formatTargetRefinements(target)

    return refinements === '' ? null : refinements
}
